import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, doc, getDoc, getDocs, query, where, orderBy } from 'firebase/firestore';
import { fetchAllGames } from '../utils/firebase';
import CasinoStatsContent from './CasinoStatsContent';
import UnboxingStatsContent from './UnboxingStatsContent';
import StockStatsContent from './StockStatsContent';
import { PgHeader, SubTabs } from './ui';

// Module-level caches — survive StatsPage unmounts so navigating away and back is instant
let _casinoStatsCache = null;
let _unboxingStatsCache = null;

function getTimeFilters(seasonConfig) {
  const filters = [
    { id: 'all', label: 'All Time' },
  ];
  // Add season-specific filters
  const seasons = seasonConfig?.seasons || {};
  const currentSeason = seasonConfig?.currentSeason || 1;
  for (const sNum of Object.keys(seasons).sort((a, b) => parseInt(b) - parseInt(a))) {
    const s = parseInt(sNum);
    filters.push({
      id: `season_${s}`,
      label: s === currentSeason ? 'This Season' : `Season ${s}`,
    });
  }
  filters.push(
    { id: 'month', label: 'Past Month' },
    { id: 'week', label: 'Past Week' },
  );
  return filters;
}

function filterGamesByTime(games, filter, seasonConfig) {
  if (filter === 'all') return games;
  const now = Date.now();
  if (filter === 'week') return games.filter(g => now - g.timestamp < 7 * 24 * 60 * 60 * 1000);
  if (filter === 'month') return games.filter(g => now - g.timestamp < 30 * 24 * 60 * 60 * 1000);
  if (filter.startsWith('season_')) {
    const seasonNum = parseInt(filter.replace('season_', ''));
    const seasons = seasonConfig?.seasons || {};
    const seasonData = seasons[String(seasonNum)];
    if (!seasonData) return games;
    const start = seasonData.startedAt || 0;
    const end = seasonData.endedAt || Date.now();
    return games.filter(g => g.timestamp >= start && g.timestamp <= end);
  }
  // Legacy "season" filter (current season)
  if (filter === 'season') {
    const seasonStart = seasonConfig?.seasonStartedAt || 0;
    return games.filter(g => g.timestamp >= seasonStart);
  }
  return games;
}

function StatsPage({ players, user, isMobile, seasonConfig }) {
  const [allGames, setAllGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statsTab, setStatsTab] = useState("game");
  const [timeFilter, setTimeFilter] = useState('all');
  const [casinoStatsAll, setCasinoStatsAll] = useState(_casinoStatsCache || []);
  const [casinoStatsLoading, setCasinoStatsLoading] = useState(false);
  const [casinoStatsLoaded, setCasinoStatsLoaded] = useState(!!_casinoStatsCache);
  const [unboxingStatsAll, setUnboxingStatsAll] = useState(_unboxingStatsCache || []);
  const [unboxingStatsLoading, setUnboxingStatsLoading] = useState(false);
  const [unboxingStatsLoaded, setUnboxingStatsLoaded] = useState(!!_unboxingStatsCache);

  const isSeason2 = true;
  const games = filterGamesByTime(allGames, timeFilter, seasonConfig);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    async function loadGames() {
      setLoading(true);
      const fetched = await fetchAllGames();
      setAllGames(fetched);
      setLoading(false);
    }
    loadGames();
  }, [user]);

  // Load casino stats when casino tab is opened, only once per session
  useEffect(() => {
    if (!user || statsTab !== "casino" || casinoStatsLoaded) return;
    async function loadCasinoStats() {
      setCasinoStatsLoading(true);
      // Build userId -> playerId map from users collection (skip test account)
      const usersSnap = await getDocs(collection(db, "users"));
      const claimedMap = {}; // playerId -> userId
      const userDataMap = {}; // userId -> user doc data
      usersSnap.docs.forEach(d => {
        const data = d.data();
        if (data.claimedPlayer && data.email !== 'test@test.com') {
          claimedMap[data.claimedPlayer] = d.id;
          userDataMap[d.id] = data;
        }
      });
      // Parallel fetch: load all players' stats + transactions concurrently
      const promises = players
        .filter(p => claimedMap[p.id])
        .map(async (p) => {
          const userId = claimedMap[p.id];
          const [statsDoc, txSnap] = await Promise.all([
            getDoc(doc(db, "casinoStats", userId)),
            getDocs(query(
              collection(db, "casinoTransactions"),
              where("userId", "==", userId),
              orderBy("timestamp", "asc")
            )),
          ]);
          const transactions = txSnap.docs.map(d => d.data());
          const userData = userDataMap[userId] || {};
          return {
            player: p,
            stats: statsDoc.exists() ? statsDoc.data() : {},
            transactions,
            crewmateTokens: userData.crewmateTokens || 0,
            imposterTokens: userData.imposterTokens || 0,
          };
        });
      const results = await Promise.all(promises);
      _casinoStatsCache = results;
      setCasinoStatsAll(results);
      setCasinoStatsLoading(false);
      setCasinoStatsLoaded(true);
    }
    loadCasinoStats();
  }, [user, statsTab, casinoStatsLoaded, players]);

  // Load unboxing stats when unboxing tab is opened, only once per session
  useEffect(() => {
    if (!user || statsTab !== "unboxing" || unboxingStatsLoaded) return;
    async function loadUnboxingStats() {
      setUnboxingStatsLoading(true);
      const usersSnap = await getDocs(collection(db, "users"));
      const claimedMap = {};
      const userDocMap = {};
      usersSnap.docs.forEach(d => {
        const data = d.data();
        if (data.claimedPlayer && data.email !== 'test@test.com') {
          claimedMap[data.claimedPlayer] = d.id;
          userDocMap[d.id] = data;
        }
      });
      const promises = players
        .filter(p => claimedMap[p.id])
        .map(async (p) => {
          const userId = claimedMap[p.id];
          const statsDoc = await getDoc(doc(db, "unboxingStats", userId));
          if (statsDoc.exists()) {
            const inventory = userDocMap[userId]?.lootboxInventory || [];
            return { player: p, stats: statsDoc.data(), inventory };
          }
          return null;
        });
      const results = (await Promise.all(promises)).filter(Boolean);
      _unboxingStatsCache = results;
      setUnboxingStatsAll(results);
      setUnboxingStatsLoading(false);
      setUnboxingStatsLoaded(true);
    }
    loadUnboxingStats();
  }, [user, statsTab, unboxingStatsLoaded, players]);

  if (loading) {
    return (
      <div className="v2-page">
        <div style={{ textAlign: 'center', padding: '80px 20px', color: 'var(--text-2)' }}>
          <div style={{ fontSize: 44, marginBottom: 14 }}>🔴</div>
          <div style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 12, letterSpacing: '0.12em' }}>LOADING STATS…</div>
        </div>
      </div>
    );
  }

  // Calculate imposter duo stats
  const duoStats = {};
  games.forEach(game => {
    if (game.impostors.length >= 2) {
      // Create all pairs of imposters
      for (let i = 0; i < game.impostors.length; i++) {
        for (let j = i + 1; j < game.impostors.length; j++) {
          const player1 = game.impostors[i];
          const player2 = game.impostors[j];
          // Sort to ensure consistent duo key
          const duoKey = [player1, player2].sort().join('|');

          if (!duoStats[duoKey]) {
            duoStats[duoKey] = { wins: 0, losses: 0, players: [player1, player2] };
          }

          if (game.didWin) {
            duoStats[duoKey].wins++;
          } else {
            duoStats[duoKey].losses++;
          }
        }
      }
    }
  });

  // Convert to array and calculate win rates
  const duos = Object.values(duoStats)
    .map(duo => ({
      ...duo,
      totalGames: duo.wins + duo.losses,
      winRate: duo.wins + duo.losses > 0
        ? ((duo.wins / (duo.wins + duo.losses)) * 100).toFixed(1)
        : 0
    }))
    .filter(duo => duo.totalGames >= 3) // Only show duos with 3+ games
    .sort((a, b) => b.winRate - a.winRate);

  const bestDuos = duos.slice(0, 5);
  const worstDuos = duos.slice(-5).reverse();

  // Calculate imposter frequency stats
  const playerFrequencies = players.map(p => {
    const imposterGames = games.filter(g => g.impostors.includes(p.id)).length;
    // Calculate actual games from games collection (same as player profiles)
    const playerGames = games.filter(g =>
      g.impostors.includes(p.id) ||
      (g.crewmates && g.crewmates.includes(p.id))
    );
    const totalGames = playerGames.length;
    const frequency = totalGames > 0 ? (imposterGames / totalGames) * 100 : 0;

    return {
      id: p.id,
      name: p.name,
      imposterGames,
      totalGames,
      frequency: frequency.toFixed(1)
    };
  }).filter(p => p.totalGames >= 10); // Only show players with 10+ games

  const mostImposter = [...playerFrequencies].sort((a, b) => b.frequency - a.frequency).slice(0, 5);
  const leastImposter = [...playerFrequencies]
    .filter(p => p.imposterGames > 0) // Only show players who have been imposter at least once
    .sort((a, b) => a.frequency - b.frequency)
    .slice(0, 5);

  // Calculate total kills from game logs only (kills only, not ejects)
  const totalKills = games.reduce((sum, g) => {
    if (!g.playerStats || !Array.isArray(g.playerStats)) return sum;
    return sum + g.playerStats.reduce((killSum, p) => killSum + (p.kills || 0), 0);
  }, 0);

  const totalTasksCompleted = games.reduce((sum, g) => {
    if (!g.playerStats || !Array.isArray(g.playerStats)) return sum;
    return sum + g.playerStats.reduce((taskSum, p) => taskSum + (!p.isImpostor ? (p.tasksCompleted || 0) : 0), 0);
  }, 0);

  const totalGames = games.length;
  const totalPlayers = players.length;

  // Calculate outcome type breakdown
  const outcomeBreakdown = {
    tasks: 0,
    kills: 0,
    sabotage: 0,
    votes: 0
  };

  games.forEach(game => {
    if (game.outcomeType === 'tasks') {
      outcomeBreakdown.tasks++;
    } else if (game.outcomeType === 'kills') {
      outcomeBreakdown.kills++;
    } else if (game.outcomeType === 'sabotage') {
      outcomeBreakdown.sabotage++;
    } else if (game.outcomeType === 'votes') {
      outcomeBreakdown.votes++;
    } else {
      outcomeBreakdown.votes++; // Old games without outcome type count as votes
    }
  });

  // Calculate overall win rates
  const imposterWins = games.filter(g => g.didWin === true).length;
  const crewmateWins = games.filter(g => g.didWin === false).length;
  const imposterWinRate = totalGames > 0 ? ((imposterWins / totalGames) * 100).toFixed(1) : 0;
  const crewmateWinRate = totalGames > 0 ? ((crewmateWins / totalGames) * 100).toFixed(1) : 0;

  // Calculate average game length (only games with duration data)
  const gamesWithDuration = games.filter(g => g.duration && g.duration > 0);
  const avgGameLengthMs = gamesWithDuration.length > 0
    ? gamesWithDuration.reduce((sum, g) => sum + g.duration, 0) / gamesWithDuration.length
    : 0;
  const avgGameLengthMin = (avgGameLengthMs / 60000).toFixed(1);

  // Calculate total time played in hours
  const totalTimePlayedMs = gamesWithDuration.reduce((sum, g) => sum + g.duration, 0);
  const totalTimePlayedHours = (totalTimePlayedMs / 3600000).toFixed(1);

  // Calculate most targeted crewmates (lowest avg kill order as crewmate)
  const crewKillOrderMap = {};
  games.forEach(g => {
    if (!g.playerStats || !Array.isArray(g.playerStats)) return;
    // Build eloChanges id->pluginName map for this game (reliable bridge)
    const eloNameMap = {};
    if (g.eloChanges && Array.isArray(g.eloChanges)) {
      g.eloChanges.forEach(e => { eloNameMap[e.name] = e.id; });
    }
    g.playerStats.forEach(ps => {
      if (ps.isImpostor || (ps.deathOrder || 0) === 0) return; // skip imposters and survivors
      // Re-rank crewmate death order for this game
      const deadCrew = g.playerStats
        .filter(p => !p.isImpostor && (p.deathOrder || 0) > 0)
        .sort((a, b) => a.deathOrder - b.deathOrder);
      const crewRank = deadCrew.findIndex(p => p.playerName === ps.playerName) + 1;
      if (crewRank === 0) return;
      // Resolve to registered player: try eloChanges bridge first, then name fallback
      const resolvedId = ps.playerId || eloNameMap[ps.playerName] || null;
      const matchedPlayer = resolvedId
        ? players.find(p => p.id === resolvedId)
        : players.find(p => p.name && p.name.toLowerCase() === ps.playerName.toLowerCase());
      if (!matchedPlayer) return;
      if (!crewKillOrderMap[matchedPlayer.id]) crewKillOrderMap[matchedPlayer.id] = { total: 0, count: 0, name: matchedPlayer.name };
      crewKillOrderMap[matchedPlayer.id].total += crewRank;
      crewKillOrderMap[matchedPlayer.id].count++;
    });
  });
  const crewKillOrderEntries = Object.values(crewKillOrderMap)
    .filter(p => p.count >= 3)
    .map(p => ({ ...p, avgKillOrder: p.total / p.count }));
  const mostTargeted = [...crewKillOrderEntries]
    .sort((a, b) => a.avgKillOrder - b.avgKillOrder)
    .slice(0, 3);
  const leastTargeted = [...crewKillOrderEntries]
    .sort((a, b) => b.avgKillOrder - a.avgKillOrder)
    .slice(0, 3);

  // Calculate avg task completion per crewmate across all games
  const taskCompletionMap = {};
  games.forEach(g => {
    if (!g.playerStats || !Array.isArray(g.playerStats)) return;
    const maxTasks = g.eloMetadata?.maxCrewTasks ||
      Math.max(0, ...g.playerStats.filter(p => !p.isImpostor).map(p => p.tasksCompleted || 0));
    if (maxTasks <= 0) return;
    const eloNameMap = {};
    if (g.eloChanges && Array.isArray(g.eloChanges)) {
      g.eloChanges.forEach(e => { eloNameMap[e.name] = e.id; });
    }
    g.playerStats.forEach(ps => {
      if (ps.isImpostor) return;
      const resolvedId = ps.playerId || eloNameMap[ps.playerName] || null;
      const matchedPlayer = resolvedId
        ? players.find(p => p.id === resolvedId)
        : players.find(p => p.name && p.name.toLowerCase() === ps.playerName.toLowerCase());
      if (!matchedPlayer) return;
      if (!taskCompletionMap[matchedPlayer.id]) taskCompletionMap[matchedPlayer.id] = { total: 0, assigned: 0, name: matchedPlayer.name };
      taskCompletionMap[matchedPlayer.id].total += ps.tasksCompleted || 0;
      taskCompletionMap[matchedPlayer.id].assigned += maxTasks;
    });
  });
  const taskCompletionEntries = Object.values(taskCompletionMap)
    .filter(p => p.assigned >= 10) // min 10 tasks assigned (roughly 2+ games)
    .map(p => ({ ...p, avgPct: (p.total / p.assigned) * 100 }));
  const highestTaskCompletion = [...taskCompletionEntries]
    .sort((a, b) => b.avgPct - a.avgPct)
    .slice(0, 3);
  const lowestTaskCompletion = [...taskCompletionEntries]
    .sort((a, b) => a.avgPct - b.avgPct)
    .slice(0, 3);

  // Calculate longest win streaks from game logs
  function calcLongestStreak(playerId, role) {
    let playerGames = games.filter(g =>
      g.impostors.includes(playerId) || (g.crewmates && g.crewmates.includes(playerId))
    );
    if (role === 'imposter') {
      playerGames = playerGames.filter(g => g.impostors.includes(playerId));
    } else if (role === 'crewmate') {
      playerGames = playerGames.filter(g =>
        (g.crewmates && g.crewmates.includes(playerId)) ||
        (!g.impostors.includes(playerId) && !g.crewmates)
      );
    }
    playerGames = playerGames.sort((a, b) => a.timestamp - b.timestamp);
    let max = 0, cur = 0;
    for (const g of playerGames) {
      const isImposter = g.impostors.includes(playerId);
      const won = (isImposter && g.didWin) || (!isImposter && !g.didWin);
      cur = won ? cur + 1 : 0;
      if (cur > max) max = cur;
    }
    return max;
  }

  function calcLongestRoleStreak(playerId, role) {
    const playerGames = games
      .filter(g => g.impostors.includes(playerId) || (g.crewmates && g.crewmates.includes(playerId)))
      .sort((a, b) => a.timestamp - b.timestamp);
    let max = 0, cur = 0;
    for (const g of playerGames) {
      const isImposter = g.impostors.includes(playerId);
      const matchesRole = role === 'imposter' ? isImposter : !isImposter;
      cur = matchesRole ? cur + 1 : 0;
      if (cur > max) max = cur;
    }
    return max;
  }

  const longestOverallStreak = players.reduce((max, p) => {
    const streak = calcLongestStreak(p.id, 'overall');
    return streak > max.streak ? { streak, player: p } : max;
  }, { streak: 0, player: null });

  const longestImposterStreak = players.reduce((max, p) => {
    const streak = calcLongestStreak(p.id, 'imposter');
    return streak > max.streak ? { streak, player: p } : max;
  }, { streak: 0, player: null });

  const longestCrewmateStreak = players.reduce((max, p) => {
    const streak = calcLongestStreak(p.id, 'crewmate');
    return streak > max.streak ? { streak, player: p } : max;
  }, { streak: 0, player: null });

  function calcLongestLossStreak(playerId, role) {
    let playerGames = games.filter(g =>
      g.impostors.includes(playerId) || (g.crewmates && g.crewmates.includes(playerId))
    );
    if (role === 'imposter') {
      playerGames = playerGames.filter(g => g.impostors.includes(playerId));
    } else if (role === 'crewmate') {
      playerGames = playerGames.filter(g =>
        (g.crewmates && g.crewmates.includes(playerId)) ||
        (!g.impostors.includes(playerId) && !g.crewmates)
      );
    }
    playerGames = playerGames.sort((a, b) => a.timestamp - b.timestamp);
    let max = 0, cur = 0;
    for (const g of playerGames) {
      const isImposter = g.impostors.includes(playerId);
      const lost = !((isImposter && g.didWin) || (!isImposter && !g.didWin));
      cur = lost ? cur + 1 : 0;
      if (cur > max) max = cur;
    }
    return max;
  }

  const longestOverallLossStreak = players.reduce((max, p) => {
    const streak = calcLongestLossStreak(p.id, 'overall');
    return streak > max.streak ? { streak, player: p } : max;
  }, { streak: 0, player: null });

  const longestImposterLossStreak = players.reduce((max, p) => {
    const streak = calcLongestLossStreak(p.id, 'imposter');
    return streak > max.streak ? { streak, player: p } : max;
  }, { streak: 0, player: null });

  const longestCrewmateLossStreak = players.reduce((max, p) => {
    const streak = calcLongestLossStreak(p.id, 'crewmate');
    return streak > max.streak ? { streak, player: p } : max;
  }, { streak: 0, player: null });

  const imposterRoleStreakRanking = players
    .map(p => ({ name: p.name, streak: calcLongestRoleStreak(p.id, 'imposter') }))
    .filter(p => p.streak > 0)
    .sort((a, b) => b.streak - a.streak)
    .slice(0, 5);

  const crewmateRoleStreakRanking = players
    .map(p => ({ name: p.name, streak: calcLongestRoleStreak(p.id, 'crewmate') }))
    .filter(p => p.streak > 0)
    .sort((a, b) => b.streak - a.streak)
    .slice(0, 5);

  // Get player name helper
  const getPlayerName = (id) => {
    const player = players.find(p => p.id === id);
    return player ? player.name : id;
  };

  // Compute favorite kill targets — for each player, find who they kill first most often,
  // then count how many players have each victim as their favorite first kill.
  const favoriteKillTargetCounts = {};
  players.forEach(p => {
    const killCounts = {};
    games.forEach(g => {
      if (!g.killEvents || !Array.isArray(g.killEvents)) return;
      let pluginName = null;
      if (g.eloChanges && Array.isArray(g.eloChanges)) {
        const entry = g.eloChanges.find(e => e.id === p.id);
        if (entry) pluginName = entry.name;
      }
      if (!pluginName) return;
      const myKills = g.killEvents
        .filter(k => k.killerName === pluginName)
        .sort((a, b) => (a.deathOrder || 0) - (b.deathOrder || 0));
      if (myKills.length === 0) return;
      const firstVictim = myKills[0].victimName;
      killCounts[firstVictim] = (killCounts[firstVictim] || 0) + 1;
    });
    let favVictim = null;
    let favCount = 0;
    for (const [victim, count] of Object.entries(killCounts)) {
      if (count > favCount) { favCount = count; favVictim = victim; }
    }
    if (favVictim) {
      // Resolve victim name to a player
      const eloNameMap = {};
      games.forEach(g => {
        if (g.eloChanges && Array.isArray(g.eloChanges)) {
          g.eloChanges.forEach(e => { eloNameMap[e.name] = e.id; });
        }
      });
      const resolvedId = eloNameMap[favVictim];
      const matchedPlayer = resolvedId
        ? players.find(pl => pl.id === resolvedId)
        : players.find(pl => pl.name && pl.name.toLowerCase() === favVictim.toLowerCase());
      const victimName = matchedPlayer ? matchedPlayer.name : favVictim;
      if (!favoriteKillTargetCounts[victimName]) favoriteKillTargetCounts[victimName] = { name: victimName, count: 0, hunters: [] };
      favoriteKillTargetCounts[victimName].count++;
      favoriteKillTargetCounts[victimName].hunters.push(p.name);
    }
  });
  const favoriteKillTargets = Object.values(favoriteKillTargetCounts)
    .sort((a, b) => b.count - a.count)
    .slice(0, 3);

  // Calculate all-time peak ELOs across all players
  const peakElos = (() => {
    const sortedGames = [...games].sort((a, b) => a.timestamp - b.timestamp);
    // Track running ELOs per player
    const playerElos = {}; // playerId -> { imp, crew }
    // Initialize from current values and walk backwards to find starting ELOs
    players.forEach(p => {
      playerElos[p.id] = { imp: p.eloImposter, crew: p.eloCrewmate };
    });
    for (let i = sortedGames.length - 1; i >= 0; i--) {
      const g = sortedGames[i];
      if (!g.eloChanges) continue;
      g.eloChanges.forEach(entry => {
        if (!playerElos[entry.id]) return;
        if (g.impostors.includes(entry.id)) playerElos[entry.id].imp = Math.round(entry.oldElo);
        else playerElos[entry.id].crew = Math.round(entry.oldElo);
      });
    }
    let peakImp = { value: 0, player: null };
    let peakCrew = { value: 0, player: null };
    let peakOverall = { value: 0, player: null };
    // Walk forward through games tracking peaks
    sortedGames.forEach(g => {
      if (!g.eloChanges) return;
      g.eloChanges.forEach(entry => {
        if (!playerElos[entry.id]) return;
        if (g.impostors.includes(entry.id)) playerElos[entry.id].imp = Math.round(entry.newElo);
        else playerElos[entry.id].crew = Math.round(entry.newElo);
        const pe = playerElos[entry.id];
        const p = players.find(pl => pl.id === entry.id);
        const name = p ? p.name : entry.name;
        if (pe.imp > peakImp.value) peakImp = { value: pe.imp, player: name };
        if (pe.crew > peakCrew.value) peakCrew = { value: pe.crew, player: name };
        const overall = Math.round((pe.imp + pe.crew) / 2);
        if (overall > peakOverall.value) peakOverall = { value: overall, player: name };
      });
    });
    return { imposter: peakImp, crewmate: peakCrew, overall: peakOverall };
  })();

  // Reusable panel style
  const panelStyle = {
    background: 'var(--bg-card)',
    borderRadius: '12px',
    border: '1px solid var(--border)',
    overflow: 'hidden',
  };
  const panelHeaderStyle = {
    padding: isMobile ? '14px 16px' : '16px 24px',
    borderBottom: '1px solid var(--border)',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  };
  const panelBodyStyle = {
    padding: isMobile ? '12px' : '20px',
  };
  const sectionTitle = (icon, text) => (
    <div style={panelHeaderStyle}>
      <span style={{ fontSize: isMobile ? '16px' : '18px' }}>{icon}</span>
      <span style={{ fontSize: isMobile ? '14px' : '16px', fontWeight: '700', color: 'var(--text-primary)', letterSpacing: '0.02em', textTransform: 'uppercase' }}>{text}</span>
    </div>
  );
  // Compact ranked row
  const rankedRow = (rank, name, detail, value, valueColor, isLast = false) => (
    <div style={{
      display: 'flex', alignItems: 'center', gap: isMobile ? '10px' : '14px',
      padding: isMobile ? '10px 0' : '12px 0',
      borderBottom: isLast ? 'none' : '1px solid rgba(255,255,255,0.04)',
    }}>
      <span style={{
        width: isMobile ? '22px' : '28px', textAlign: 'center',
        fontSize: isMobile ? '13px' : '15px', fontWeight: '800',
        color: rank <= 3 ? '#FBBF24' : 'var(--text-dim)',
      }}>
        {rank}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: isMobile ? '14px' : '16px', fontWeight: '600', color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</div>
        {detail && <div style={{ fontSize: isMobile ? '12px' : '13px', color: 'var(--text-secondary)', marginTop: '2px' }}>{detail}</div>}
      </div>
      <span style={{ fontSize: isMobile ? '15px' : '18px', fontWeight: '800', color: valueColor, flexShrink: 0 }}>{value}</span>
    </div>
  );
  const emptyState = (msg) => (
    <div style={{ padding: isMobile ? '20px' : '32px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '14px' }}>{msg}</div>
  );

  const statsTabs = [
    { id: 'game',     label: isMobile ? '🎮 Game'     : '🎮 Game Stats' },
    { id: 'casino',   label: isMobile ? '🎰 Casino'   : '🎰 Casino Stats' },
    { id: 'unboxing', label: isMobile ? '📦 Unbox'    : '📦 Unboxing Stats' },
    { id: 'stocks',   label: isMobile ? '📈 Stocks'   : '📈 Stock Stats' },
  ];

  return (
    <div className="v2-page">
      <PgHeader title="Statistics" sub="Player performance, casino, unboxing, and stock activity" />

      <SubTabs tabs={statsTabs} active={statsTab} onChange={setStatsTab} />

      {/* Time Filter — game stats only */}
      {statsTab === "game" && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: isMobile ? '20px' : '24px', flexWrap: 'wrap' }}>
          {getTimeFilters(seasonConfig).map(f => (
            <button key={f.id} onClick={() => setTimeFilter(f.id)} style={{
              padding: isMobile ? '6px 12px' : '5px 14px',
              background: timeFilter === f.id ? 'rgba(232, 56, 74, 0.15)' : 'transparent',
              border: `1px solid ${timeFilter === f.id ? 'var(--red)' : 'var(--border)'}`,
              borderRadius: '6px',
              color: timeFilter === f.id ? 'var(--red)' : 'var(--text-dim)',
              fontWeight: timeFilter === f.id ? '700' : '500',
              fontSize: '12px',
              cursor: 'pointer',
              minHeight: isMobile ? '44px' : undefined,
              transition: 'all 0.15s ease',
            }}>
              {f.label}
            </button>
          ))}
        </div>
      )}

      {statsTab === "game" && <>

      {/* ═══ HERO STATS ROW ═══ */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
        gap: isMobile ? '8px' : '12px',
        marginBottom: isMobile ? '16px' : '24px',
      }}>
        {[
          { label: 'Games Played', value: totalGames, color: '#e8384a' },
          { label: 'Players', value: totalPlayers, color: '#3a8fd4' },
          { label: 'Total Kills', value: totalKills, color: '#f44336' },
          { label: 'Tasks Done', value: totalTasksCompleted, color: '#4CAF50' },
          { label: 'Avg Game', value: avgGameLengthMs > 0 ? `${avgGameLengthMin}m` : 'N/A', color: '#22D3EE' },
          { label: 'Time Played', value: totalTimePlayedMs > 0 ? `${totalTimePlayedHours}h` : 'N/A', color: '#FBBF24' },
        ].map((s, idx) => (
          <div key={idx} style={{
            background: 'var(--bg-card)',
            border: '1px solid var(--border)',
            borderLeft: `3px solid ${s.color}`,
            borderRadius: '8px',
            padding: isMobile ? '12px' : '16px 20px',
          }}>
            <div style={{ fontSize: isMobile ? '10px' : '11px', fontWeight: '600', color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '4px' }}>
              {s.label}
            </div>
            <div style={{ fontSize: isMobile ? '22px' : '28px', fontWeight: '800', color: 'var(--text-primary)', fontFamily: "'Orbitron', sans-serif" }}>
              {s.value}
            </div>
          </div>
        ))}
      </div>

      {/* ═══ WIN RATES + OUTCOMES ═══ */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
        gap: isMobile ? '12px' : '16px',
        marginBottom: isMobile ? '16px' : '24px',
      }}>
        {/* Win Rates */}
        <div style={panelStyle}>
          {sectionTitle('⚔️', 'Win Rates')}
          <div style={panelBodyStyle}>
            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                <span style={{ fontSize: '13px', color: '#e8384a', fontWeight: '600' }}>🔴 Impostor</span>
                <span style={{ fontSize: '13px', color: '#e8384a', fontWeight: '800' }}>{imposterWinRate}%</span>
              </div>
              <div style={{ height: '8px', background: 'rgba(255,255,255,0.06)', borderRadius: '4px', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${imposterWinRate}%`, background: 'linear-gradient(90deg, #e8384a, #ff6b7a)', borderRadius: '4px', transition: 'width 0.6s ease' }} />
              </div>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>{imposterWins} / {totalGames} wins</div>
            </div>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                <span style={{ fontSize: '13px', color: '#3a8fd4', fontWeight: '600' }}>🔵 Crewmate</span>
                <span style={{ fontSize: '13px', color: '#3a8fd4', fontWeight: '800' }}>{crewmateWinRate}%</span>
              </div>
              <div style={{ height: '8px', background: 'rgba(255,255,255,0.06)', borderRadius: '4px', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${crewmateWinRate}%`, background: 'linear-gradient(90deg, #3a8fd4, #6bb5e8)', borderRadius: '4px', transition: 'width 0.6s ease' }} />
              </div>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>{crewmateWins} / {totalGames} wins</div>
            </div>
            <div style={{ marginTop: '12px', fontSize: '12px', color: 'var(--text-secondary)' }}>
              Avg {totalGames > 0 ? (totalKills / totalGames).toFixed(1) : 0} kills/game
            </div>
          </div>
        </div>

        {/* Outcome Breakdown */}
        <div style={panelStyle}>
          {sectionTitle('🎯', 'Outcomes')}
          <div style={panelBodyStyle}>
            {[
              { label: 'Tasks', icon: '✅', count: outcomeBreakdown.tasks, color: '#4CAF50' },
              { label: 'Kills', icon: '🔪', count: outcomeBreakdown.kills, color: '#f44336' },
              { label: 'Sabotage', icon: '💣', count: outcomeBreakdown.sabotage, color: '#FF9800' },
              { label: 'Votes', icon: '🗳️', count: outcomeBreakdown.votes, color: '#9C27B0' },
            ].map((o, idx) => {
              const pct = totalGames > 0 ? ((o.count / totalGames) * 100) : 0;
              return (
                <div key={idx} style={{ marginBottom: idx < 3 ? '10px' : 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <span style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: '600' }}>{o.icon} {o.label}</span>
                    <span style={{ fontSize: '12px', color: 'var(--text-primary)', fontWeight: '700' }}>{o.count} <span style={{ color: 'var(--text-dim)' }}>({pct.toFixed(1)}%)</span></span>
                  </div>
                  <div style={{ height: '6px', background: 'rgba(255,255,255,0.06)', borderRadius: '3px', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${pct}%`, background: o.color, borderRadius: '3px', transition: 'width 0.6s ease' }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ═══ PEAK ELO RECORDS ═══ */}
      <div style={{ ...panelStyle, marginBottom: isMobile ? '16px' : '24px' }}>
        {sectionTitle('👑', 'Peak ELO Records')}
        <div style={{ ...panelBodyStyle, display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: isMobile ? '12px' : '16px' }}>
          {[
            { label: 'Imposter', value: peakElos.imposter.value, player: peakElos.imposter.player, color: '#e8384a', border: '#e8384a' },
            { label: 'Crewmate', value: peakElos.crewmate.value, player: peakElos.crewmate.player, color: '#3a8fd4', border: '#3a8fd4' },
            { label: 'Overall', value: peakElos.overall.value, player: peakElos.overall.player, color: '#FBBF24', border: '#FBBF24' },
          ].map((elo, idx) => (
            <div key={idx} style={{
              background: 'var(--bg-elevated)',
              borderRadius: '8px',
              padding: isMobile ? '14px' : '20px',
              borderTop: `3px solid ${elo.border}`,
              textAlign: 'center',
            }}>
              <div style={{ fontSize: '11px', fontWeight: '700', color: elo.color, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '8px' }}>{elo.label}</div>
              <div style={{ fontSize: isMobile ? '28px' : '36px', fontWeight: '800', color: 'var(--text-primary)', fontFamily: "'Orbitron', sans-serif" }}>{elo.value || 'N/A'}</div>
              {elo.player && <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>{elo.player}</div>}
            </div>
          ))}
        </div>
      </div>

      {/* ═══ STREAKS (Win + Loss combined) ═══ */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
        gap: isMobile ? '12px' : '16px',
        marginBottom: isMobile ? '16px' : '24px',
      }}>
        {/* Win Streaks */}
        <div style={panelStyle}>
          {sectionTitle('🔥', 'Win Streaks')}
          <div style={panelBodyStyle}>
            {[
              { label: 'Overall', streak: longestOverallStreak, color: '#FBBF24' },
              { label: 'Impostor', streak: longestImposterStreak, color: '#e8384a' },
              { label: 'Crewmate', streak: longestCrewmateStreak, color: '#3a8fd4' },
            ].map((s, idx) => (
              <div key={idx} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: isMobile ? '10px 0' : '12px 0',
                borderBottom: idx < 2 ? '1px solid rgba(255,255,255,0.04)' : 'none',
              }}>
                <div>
                  <div style={{ fontSize: isMobile ? '12px' : '13px', fontWeight: '600', color: s.color, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '2px' }}>{s.label}</div>
                  {s.streak.player && <div style={{ fontSize: isMobile ? '14px' : '16px', fontWeight: '700', color: 'var(--text-primary)' }}>{s.streak.player.name}</div>}
                </div>
                <span style={{ fontSize: isMobile ? '22px' : '28px', fontWeight: '800', color: s.color, fontFamily: "'Orbitron', sans-serif" }}>{s.streak.streak}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Loss Streaks */}
        <div style={panelStyle}>
          {sectionTitle('💀', 'Loss Streaks')}
          <div style={panelBodyStyle}>
            {[
              { label: 'Overall', streak: longestOverallLossStreak, color: '#888' },
              { label: 'Impostor', streak: longestImposterLossStreak, color: '#8B0000' },
              { label: 'Crewmate', streak: longestCrewmateLossStreak, color: '#1a5276' },
            ].map((s, idx) => (
              <div key={idx} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: isMobile ? '10px 0' : '12px 0',
                borderBottom: idx < 2 ? '1px solid rgba(255,255,255,0.04)' : 'none',
              }}>
                <div>
                  <div style={{ fontSize: isMobile ? '12px' : '13px', fontWeight: '600', color: s.color, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '2px' }}>{s.label}</div>
                  {s.streak.player && <div style={{ fontSize: isMobile ? '14px' : '16px', fontWeight: '700', color: 'var(--text-primary)' }}>{s.streak.player.name}</div>}
                </div>
                <span style={{ fontSize: isMobile ? '22px' : '28px', fontWeight: '800', color: s.color, fontFamily: "'Orbitron', sans-serif" }}>{s.streak.streak}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ═══ IMPOSTOR DUOS (Best + Worst side by side) ═══ */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
        gap: isMobile ? '12px' : '16px',
        marginBottom: isMobile ? '16px' : '24px',
      }}>
        <div style={panelStyle}>
          {sectionTitle('🔥', 'Best Duos')}
          <div style={panelBodyStyle}>
            {bestDuos.length > 0 ? bestDuos.map((duo, i) => (
              rankedRow(
                i + 1,
                `${getPlayerName(duo.players[0])} + ${getPlayerName(duo.players[1])}`,
                `${duo.wins}W - ${duo.losses}L (${duo.totalGames}g)`,
                `${duo.winRate}%`,
                '#4CAF50',
                i === bestDuos.length - 1
              )
            )) : emptyState('Not enough duo data yet.')}
          </div>
        </div>

        <div style={panelStyle}>
          {sectionTitle('💀', 'Worst Duos')}
          <div style={panelBodyStyle}>
            {worstDuos.length > 0 ? worstDuos.map((duo, i) => (
              rankedRow(
                i + 1,
                `${getPlayerName(duo.players[0])} + ${getPlayerName(duo.players[1])}`,
                `${duo.wins}W - ${duo.losses}L (${duo.totalGames}g)`,
                `${duo.winRate}%`,
                '#f44336',
                i === worstDuos.length - 1
              )
            )) : emptyState('Not enough duo data yet.')}
          </div>
        </div>
      </div>

      {/* ═══ IMPOSTOR FREQUENCY (Most + Least side by side) ═══ */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
        gap: isMobile ? '12px' : '16px',
        marginBottom: isMobile ? '16px' : '24px',
      }}>
        <div style={panelStyle}>
          {sectionTitle('🔴', 'Most Often Impostor')}
          <div style={panelBodyStyle}>
            {mostImposter.length > 0 ? mostImposter.map((p, i) => (
              rankedRow(i + 1, p.name, `${p.imposterGames}/${p.totalGames} games`, `${p.frequency}%`, '#f44336', i === mostImposter.length - 1)
            )) : emptyState('Not enough game data yet.')}
          </div>
        </div>

        <div style={panelStyle}>
          {sectionTitle('🔵', 'Least Often Impostor')}
          <div style={panelBodyStyle}>
            {leastImposter.length > 0 ? leastImposter.map((p, i) => (
              rankedRow(i + 1, p.name, `${p.imposterGames}/${p.totalGames} games`, `${p.frequency}%`, '#2196F3', i === leastImposter.length - 1)
            )) : emptyState('Not enough game data yet.')}
          </div>
        </div>
      </div>

      {/* ═══ TARGETING (Most + Least + Kill Targets) ═══ */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr',
        gap: isMobile ? '12px' : '16px',
        marginBottom: isMobile ? '16px' : '24px',
      }}>
        <div style={panelStyle}>
          {sectionTitle('🎯', 'Most Targeted')}
          <div style={{ ...panelBodyStyle, fontSize: '12px', color: 'var(--text-secondary)', paddingBottom: '4px' }}>Lowest avg kill order (killed earliest)</div>
          <div style={{ padding: isMobile ? '0 12px 12px' : '0 20px 20px' }}>
            {mostTargeted.length > 0 ? mostTargeted.map((p, i) => (
              rankedRow(i + 1, p.name, `${p.count} deaths`, `#${p.avgKillOrder.toFixed(1)}`, '#f44336', i === mostTargeted.length - 1)
            )) : emptyState('Need 3+ tracked deaths.')}
          </div>
        </div>

        <div style={panelStyle}>
          {sectionTitle('🛡️', 'Least Targeted')}
          <div style={{ ...panelBodyStyle, fontSize: '12px', color: 'var(--text-secondary)', paddingBottom: '4px' }}>Highest avg kill order (killed latest)</div>
          <div style={{ padding: isMobile ? '0 12px 12px' : '0 20px 20px' }}>
            {leastTargeted.length > 0 ? leastTargeted.map((p, i) => (
              rankedRow(i + 1, p.name, `${p.count} deaths`, `#${p.avgKillOrder.toFixed(1)}`, '#4caf50', i === leastTargeted.length - 1)
            )) : emptyState('Need 3+ tracked deaths.')}
          </div>
        </div>

        <div style={panelStyle}>
          {sectionTitle('🔪', 'Kill Magnets')}
          <div style={{ ...panelBodyStyle, fontSize: '12px', color: 'var(--text-secondary)', paddingBottom: '4px' }}>#1 first-kill target for the most players</div>
          <div style={{ padding: isMobile ? '0 12px 12px' : '0 20px 20px' }}>
            {favoriteKillTargets.length > 0 ? favoriteKillTargets.map((p, i) => (
              rankedRow(i + 1, p.name, `Hunted by: ${p.hunters.join(', ')}`, `${p.count}`, '#f44336', i === favoriteKillTargets.length - 1)
            )) : emptyState('Not enough kill data yet.')}
          </div>
        </div>
      </div>

      {/* ═══ TASK COMPLETION (Highest + Lowest side by side) ═══ */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
        gap: isMobile ? '12px' : '16px',
        marginBottom: isMobile ? '16px' : '24px',
      }}>
        <div style={panelStyle}>
          {sectionTitle('✅', 'Best Task Completion')}
          <div style={{ ...panelBodyStyle, fontSize: '12px', color: 'var(--text-secondary)', paddingBottom: '4px' }}>Highest avg completion rate (min. 10 tasks)</div>
          <div style={{ padding: isMobile ? '0 12px 12px' : '0 20px 20px' }}>
            {highestTaskCompletion.length > 0 ? highestTaskCompletion.map((p, i) => (
              rankedRow(i + 1, p.name, `${p.total}/${p.assigned} tasks`, `${p.avgPct.toFixed(1)}%`, '#4caf50', i === highestTaskCompletion.length - 1)
            )) : emptyState('Not enough task data yet.')}
          </div>
        </div>

        <div style={panelStyle}>
          {sectionTitle('📋', 'Worst Task Completion')}
          <div style={{ ...panelBodyStyle, fontSize: '12px', color: 'var(--text-secondary)', paddingBottom: '4px' }}>Lowest avg completion rate (min. 10 tasks)</div>
          <div style={{ padding: isMobile ? '0 12px 12px' : '0 20px 20px' }}>
            {lowestTaskCompletion.length > 0 ? lowestTaskCompletion.map((p, i) => (
              rankedRow(i + 1, p.name, `${p.total}/${p.assigned} tasks`, `${p.avgPct.toFixed(1)}%`, '#f44336', i === lowestTaskCompletion.length - 1)
            )) : emptyState('Not enough task data yet.')}
          </div>
        </div>
      </div>

      {/* ═══ ROLE STREAKS (Impostor + Crewmate in a row) ═══ */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
        gap: isMobile ? '12px' : '16px',
        marginBottom: isMobile ? '16px' : '24px',
      }}>
        <div style={panelStyle}>
          {sectionTitle('🔴', 'Most Impostor in a Row')}
          <div style={panelBodyStyle}>
            {imposterRoleStreakRanking.length > 0 ? imposterRoleStreakRanking.map((p, i) => (
              rankedRow(i + 1, p.name, null, p.streak, '#E84855', i === imposterRoleStreakRanking.length - 1)
            )) : emptyState('Not enough game data yet.')}
          </div>
        </div>

        <div style={panelStyle}>
          {sectionTitle('🔵', 'Most Crewmate in a Row')}
          <div style={panelBodyStyle}>
            {crewmateRoleStreakRanking.length > 0 ? crewmateRoleStreakRanking.map((p, i) => (
              rankedRow(i + 1, p.name, null, p.streak, '#3A8FD4', i === crewmateRoleStreakRanking.length - 1)
            )) : emptyState('Not enough game data yet.')}
          </div>
        </div>
      </div>

      </>}

      {statsTab === "casino" && casinoStatsLoading && (
        <p style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '40px' }}>Loading casino stats...</p>
      )}
      {statsTab === "casino" && !casinoStatsLoading && casinoStatsAll.length === 0 && (
        <p style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '40px' }}>No casino data yet.</p>
      )}
      {statsTab === "casino" && !casinoStatsLoading && casinoStatsAll.length > 0 && (
        <CasinoStatsContent casinoStatsAll={casinoStatsAll} seasonConfig={seasonConfig} />
      )}

      {statsTab === "unboxing" && unboxingStatsLoading && (
        <p style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '40px' }}>Loading unboxing stats...</p>
      )}
      {statsTab === "unboxing" && !unboxingStatsLoading && unboxingStatsAll.length === 0 && (
        <p style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '40px' }}>No unboxing data yet.</p>
      )}
      {statsTab === "unboxing" && !unboxingStatsLoading && unboxingStatsAll.length > 0 && (
        <UnboxingStatsContent unboxingStatsAll={unboxingStatsAll} isS2={isSeason2} />
      )}

      {statsTab === "stocks" && (
        <StockStatsContent />
      )}

    </div>
  );
}

export default StatsPage;
